import type { AlfredDeviceConfigKey } from "@/types/alfred-device"

type NonPresentationKey =
  | "id"
  | "inheritedFromGlobal"
  | "createdAt"
  | "updatedAt"
  | "updatedBy"

export type AlfredDeviceFieldKey =
  | Exclude<AlfredDeviceConfigKey, NonPresentationKey>
  | "ticketmasterConsumerKey"
  | "configurationPassword"

export interface AlfredDeviceFieldPresentation {
  key: AlfredDeviceFieldKey
  label: string
}

export interface AlfredDeviceFieldSection {
  id: string
  title: string
  fields: readonly AlfredDeviceFieldPresentation[]
}

export type PresentedAlfredConfigField<T> = T & {
  label: string
  section: string
}

export const ALFRED_DEVICE_SECTION_TITLES = {
  deviceIdentity: "Device Identity",
  validationProvider: "Validation Provider",
  proVenueSettings: "ProVenue Settings",
  deviceLocation: "Device Location",
  offlineValidation: "Offline Validation",
  sounds: "Sounds",
  temperatureAndCooling: "Temperature & Cooling",
  validationFlow: "Validation Flow",
  timeouts: "Timeouts",
  operatingModes: "Operating Modes",
  appearance: "Appearance",
  features: "Features",
  vendorCredentials: "Vendor Credentials",
  displayConfiguration: "Display Configuration",
  ticketmasterSettings: "Ticketmaster Settings",
  security: "Security",
} as const

/**
 * Shared presentation contract for Add, Clone, and full-page Edit.
 * Array order is display order, while API property names remain internal keys.
 */
export const ALFRED_DEVICE_FIELD_SECTIONS = [
  {
    id: "deviceIdentity",
    title: ALFRED_DEVICE_SECTION_TITLES.deviceIdentity,
    fields: [
      { key: "deviceId", label: "Device ID" },
      { key: "clientId", label: "Client" },
      { key: "sotiDeviceId", label: "SOTI Device ID" },
      { key: "venueCode", label: "Venue Code" },
      { key: "league", label: "League" },
    ],
  },
  {
    id: "validationProvider",
    title: ALFRED_DEVICE_SECTION_TITLES.validationProvider,
    fields: [{ key: "validationVendor", label: "Validation Vendor" }],
  },
  {
    id: "proVenueSettings",
    title: ALFRED_DEVICE_SECTION_TITLES.proVenueSettings,
    fields: [
      { key: "proVenueUser", label: "ProVenue User" },
      { key: "proVenueInstances", label: "ProVenue Instances" },
    ],
  },
  {
    id: "deviceLocation",
    title: ALFRED_DEVICE_SECTION_TITLES.deviceLocation,
    fields: [
      { key: "facilityCode", label: "Facility Code" },
      { key: "areaCode", label: "Area Code" },
      { key: "deviceCode", label: "Device Code" },
    ],
  },
  {
    id: "offlineValidation",
    title: ALFRED_DEVICE_SECTION_TITLES.offlineValidation,
    fields: [
      { key: "offlineValidationMode", label: "Force Offline Validation" },
      { key: "offlineBatchUploadLimit", label: "Offline Batch Upload Limit" },
    ],
  },
  {
    id: "sounds",
    title: ALFRED_DEVICE_SECTION_TITLES.sounds,
    fields: [
      { key: "soundValidNumber", label: "Valid Sound" },
      { key: "soundInvalidNumber", label: "Invalid Sound" },
      { key: "soundWarningNumber", label: "Warning Sound" },
    ],
  },
  {
    id: "temperatureAndCooling",
    title: ALFRED_DEVICE_SECTION_TITLES.temperatureAndCooling,
    fields: [
      { key: "maxCpuTemperature", label: "Max CPU Temperature (°C)" },
      { key: "minCpuTemperature", label: "Min CPU Temperature (°C)" },
      { key: "enableFan", label: "Enable Fan" },
    ],
  },
  {
    id: "validationFlow",
    title: ALFRED_DEVICE_SECTION_TITLES.validationFlow,
    fields: [
      { key: "exitScanningAllowed", label: "Exit Scanning Allowed" },
      { key: "useSimpleResponse", label: "Simplified Validation Display" },
    ],
  },
  {
    id: "timeouts",
    title: ALFRED_DEVICE_SECTION_TITLES.timeouts,
    fields: [
      { key: "adminModeIdleWindow", label: "Admin Mode Idle Window" },
      { key: "validDisplayTime", label: "Valid Display Time" },
      { key: "invalidDisplayTime", label: "Invalid Display Time" },
      { key: "unlockedAccessTime", label: "Unlocked Access Time" },
    ],
  },
  {
    id: "operatingModes",
    title: ALFRED_DEVICE_SECTION_TITLES.operatingModes,
    fields: [
      { key: "operatingMode", label: "Operating Mode" },
      { key: "turnstileMode", label: "Turnstile Mode" },
      { key: "isLockFailSafe", label: "Fail Safe Lock Mode" },
    ],
  },
  {
    id: "appearance",
    title: ALFRED_DEVICE_SECTION_TITLES.appearance,
    fields: [
      { key: "mainBackgroundColor", label: "Main Background Color" },
      { key: "secondaryBackgroundColor", label: "Secondary Background Color" },
      { key: "idleStateColor", label: "Idle State Color" },
    ],
  },
  {
    id: "features",
    title: ALFRED_DEVICE_SECTION_TITLES.features,
    fields: [
      { key: "nfcEnabled", label: "Enable NFC Validation" },
      { key: "enableHotspot", label: "Enable Hotspot" },
      { key: "wakeLockEnabled", label: "Keep Screen Awake" },
      { key: "enableScanReset", label: "Enable Scan Reset Feature" },
      { key: "enableManualValidationMask", label: "Enable Manual Validation" },
    ],
  },
  {
    id: "vendorCredentials",
    title: ALFRED_DEVICE_SECTION_TITLES.vendorCredentials,
    fields: [
      { key: "appleVendors", label: "Apple Vendors" },
      { key: "googleVendors", label: "Google Vendors" },
    ],
  },
  {
    id: "displayConfiguration",
    title: ALFRED_DEVICE_SECTION_TITLES.displayConfiguration,
    fields: [{ key: "displayConfiguration", label: "Display Configuration" }],
  },
  {
    id: "ticketmasterSettings",
    title: ALFRED_DEVICE_SECTION_TITLES.ticketmasterSettings,
    fields: [
      { key: "ticketmasterVenueId", label: "Ticketmaster Venue ID" },
      { key: "ticketmasterConsumerKey", label: "Ticketmaster Consumer Key" },
    ],
  },
  {
    id: "security",
    title: ALFRED_DEVICE_SECTION_TITLES.security,
    fields: [{ key: "configurationPassword", label: "Configuration Password" }],
  },
] as const satisfies readonly AlfredDeviceFieldSection[]

/**
 * Applies the shared labels, sections, and display order to any supported
 * subset of Alfred configuration fields. Device configuration requires the
 * complete schema; global defaults intentionally omit device identity fields.
 */
export function presentAlfredConfigFields<T extends { key: string }>(
  fields: readonly T[],
  { requireCompleteSchema = false }: { requireCompleteSchema?: boolean } = {},
): PresentedAlfredConfigField<T>[] {
  const fieldsByKey = new Map<string, T>()
  for (const field of fields) {
    if (fieldsByKey.has(field.key)) {
      throw new Error(`Duplicate Alfred field behavior for ${field.key}`)
    }
    fieldsByKey.set(field.key, field)
  }

  const presented: PresentedAlfredConfigField<T>[] = []
  for (const section of ALFRED_DEVICE_FIELD_SECTIONS) {
    for (const presentation of section.fields as readonly AlfredDeviceFieldPresentation[]) {
      const field = fieldsByKey.get(presentation.key)
      if (!field) {
        if (requireCompleteSchema) {
          throw new Error(`Missing field behavior for ${presentation.key}`)
        }
        continue
      }

      presented.push({
        ...field,
        label: presentation.label,
        section: section.title,
      })
      fieldsByKey.delete(presentation.key)
    }
  }

  if (fieldsByKey.size > 0) {
    throw new Error(
      `Missing Alfred field presentation for ${[...fieldsByKey.keys()].join(", ")}`,
    )
  }

  return presented
}

export function getAlfredDeviceFieldLabel(key: AlfredDeviceFieldKey): string {
  for (const section of ALFRED_DEVICE_FIELD_SECTIONS) {
    const field = (section.fields as readonly AlfredDeviceFieldPresentation[]).find(
      (candidate) => candidate.key === key,
    )
    if (field) return field.label
  }

  throw new Error(`Missing Alfred device field presentation for ${key}`)
}
